import { gql } from 'graphql-request';
import { mapActions, mapGetters } from 'vuex';
import lodash from 'lodash';

export default {
  computed: {
    examinationList() {
      const result = this.$t('medicalProcedures.prevention', { returnObjects: true });
      return Array.isArray(result) ? result : [];
    },
    vaccinationList() {
      const result = this.$t('medicalProcedures.vaccination', { returnObjects: true });
      return Array.isArray(result) ? result : [];
    },
    medicalProcedureTexts() {
      const data = this.medicalProcedureData;
      if (!data) {
        return {};
      }

      const fieldMap =
        data.type === 'examination'
          ? {
              perex: 'perex',
              expectation: 'expectation',
              preparation: 'preparation',
              isItPaid: 'isItPaid',
              how: 'how',
              when: 'when',
              findConcerningQuestion: 'findConcerningQuestion'
            }
          : {
              perex: 'perex',
              transmission: 'transmission',
              prevention: 'prevention',
              vaccineChoice: 'vaccineChoice'
            };

      const texts = {};
      for (const [key, field] of Object.entries(fieldMap)) {
        if (data[field]) {
          texts[key] = data[field];
        }
      }

      return texts;
    },
    personalizationEnabled() {
      const personalizationEnabledSettings = this.userAppData.find(
        val => val.name === 'preventionPersonalizationEnabled'
      );
      return !personalizationEnabledSettings
        ? true
        : personalizationEnabledSettings.value === 'true';
    },
    sortedExaminations() {
      const examinationSorting = input => {
        return [
          //Required, missed
          ...input.filter(
            mapValue => this.isRequired(mapValue) && this.isAfterScheduledDate(mapValue)
          ),
          //Required, empty
          ...input.filter(mapValue => this.isRequired(mapValue) && this.isNotFilled(mapValue)),
          //Optional, missed
          ...input.filter(
            mapValue => !this.isRequired(mapValue) && this.isAfterScheduledDate(mapValue)
          ),
          //Optional, empty
          ...input.filter(mapValue => !this.isRequired(mapValue) && this.isNotFilled(mapValue)),
          //Required, ok
          ...input.filter(
            mapValue =>
              this.isRequired(mapValue) &&
              !this.isAfterScheduledDate(mapValue) &&
              !this.isNotFilled(mapValue)
          ),
          //Optional, ok
          ...input.filter(
            mapValue =>
              !this.isRequired(mapValue) &&
              !this.isAfterScheduledDate(mapValue) &&
              !this.isNotFilled(mapValue)
          )
        ];
      };

      return [...examinationSorting(this.examinations)];
    },
    sortedVaccinations() {
      const vaccinationSorting = input => {
        return [
          //Interval, missed
          ...input.filter(
            mapValue => this.hasInterval(mapValue) && this.isAfterScheduledDate(mapValue)
          ),
          //Interval, empty
          ...input.filter(mapValue => this.hasInterval(mapValue) && !mapValue.lastReport),
          //Without Interval, empty
          ...input.filter(mapValue => !this.hasInterval(mapValue) && !mapValue.lastReport),
          //Interval, ok
          ...input.filter(
            mapValue =>
              this.hasInterval(mapValue) &&
              !this.isAfterScheduledDate(mapValue) &&
              !!mapValue.lastReport
          ),
          //Without Interval, ok
          ...input.filter(
            mapValue =>
              !this.hasInterval(mapValue) &&
              !this.isAfterScheduledDate(mapValue) &&
              !!mapValue.lastReport
          )
        ];
      };
      return [...vaccinationSorting(this.vaccinations)];
    },
    reportsLoading: {
      get() {
        return this.$store.state.profile.prevention.reportsLoading;
      },
      set(value) {
        this.$store.commit('profile/prevention/setReportsLoading', value);
      }
    },
    appointmentsLoading: {
      get() {
        return this.$store.state.profile.prevention.appointmentsLoading;
      },
      set(value) {
        this.$store.commit('profile/prevention/setAppointmentsLoading', value);
      }
    },
    ...mapGetters({
      examinations: 'profile/prevention/examinations',
      vaccinations: 'profile/prevention/vaccinations',
      reports: 'profile/prevention/reports',
      appointments: 'profile/prevention/appointments',
      userAppData: 'profile/userAppData/userAppData'
    })
  },
  methods: {
    async getPreventionData() {
      try {
        const query = gql`
          query($gender: Gender,
            $yearOfBirth: Int,
            $regionCode: String
          ) {
            listExaminations(gender: $gender, yearOfBirth: $yearOfBirth, regionCode: $regionCode) {
              items {
                id,
                category,
                recommendation,
                availabilityRule {
                  gender,
                  maxAge,
                  minAge
                },
                intervalRules {
                  interval
                },
                type
              }
            }
            listExaminationsScheduled(profileId: "${this.$auth.user.profileId}") {
              items {
                procedureInfoId,
                scheduledDate
              }
            }
            listLastExaminationReports(profileId: "${this.$auth.user.profileId}") {
              items {
                date,
                procedureInfoId
              }
            }
            listNextExaminationAppointment(profileId: "${this.$auth.user.profileId}") {
              items {
                dateTime,
                procedureInfoId
              }
            }
            listVaccinations(regionCode: $regionCode) {
              items {
                id,
                recommendation,
                intervalRules {
                  interval
                },
                type
              }
            }
            listVaccinationsScheduled(profileId: "${this.$auth.user.profileId}") {
              items {
                procedureInfoId,
                scheduledDate
              }
            }
            listLastVaccinationReports(profileId: "${this.$auth.user.profileId}") {
              items {
                date,
                procedureInfoId
              }
            }
            listNextVaccinationAppointment(profileId: "${this.$auth.user.profileId}") {
              items {
                dateTime,
                procedureInfoId
              }
            }
          }
        `;

        let gender;
        switch (this.$auth.user.gender) {
          case 1:
            gender = 'MALE';
            break;
          case 2:
            gender = 'FEMALE';
            break;
          default:
            gender = 'OTHER';
        }

        let variables = {
          gender,
          yearOfBirth: null,
          regionCode: this.webuserRegion
        };

        if (this.personalizationEnabled && this.$auth.user.birthDate) {
          variables.yearOfBirth = this.dateToText(this.$auth.user.birthDate, { year: 'numeric' });
        }

        const medicalProceduresMapper = (
          baseContainingObj,
          scheduledContainingObj,
          lastReportContainingObj,
          nextAppointmentContainingObj,
          staticList
        ) => {
          const scheduled = scheduledContainingObj?.items || [];
          const lastReports = lastReportContainingObj?.items || [];
          const nextAppts = nextAppointmentContainingObj?.items || [];
          const staticItems = Array.isArray(staticList) ? staticList.filter(li => li.id) : [];

          return (baseContainingObj?.items || [])
            .filter(item => staticItems.some(li => li.id === item.id))
            .map(item => {
              const scheduledReport = scheduled.find(li => li.procedureInfoId === item.id);
              const lastReport = lastReports.find(li => li.procedureInfoId === item.id);
              const nextAppointment = nextAppts.find(li => li.procedureInfoId === item.id);

              const staticData = staticItems.find(li => li.id === item.id);

              return {
                ...item,
                type: item.type.toLowerCase(),
                ...staticData,
                title: staticData.title || staticData.name,
                recommendation: item.recommendation === 'OPTIONAL' ? 'optional' : 'recommended',
                scheduledReport: scheduledReport ? scheduledReport.scheduledDate : null,
                lastReport: lastReport ? lastReport.date : null,
                scheduledMissed:
                  scheduledReport && new Date(scheduledReport.scheduledDate) < new Date(),
                nextAppointment: nextAppointment ? nextAppointment.dateTime : null,
                nextAppointmentMissed:
                  nextAppointment && new Date(nextAppointment.dateTime) < new Date()
              };
            });
        };

        const {
          listExaminations,
          listExaminationsScheduled,
          listLastExaminationReports,
          listNextExaminationAppointment,
          listVaccinations,
          listVaccinationsScheduled,
          listLastVaccinationReports,
          listNextVaccinationAppointment
        } = await this.$graphql.medicalProcedures.request(query, variables);

        await this.setExaminations(
          medicalProceduresMapper(
            listExaminations,
            listExaminationsScheduled,
            listLastExaminationReports,
            listNextExaminationAppointment,
            this.examinationList
          )
        );
        await this.setVaccinations(
          medicalProceduresMapper(
            listVaccinations,
            listVaccinationsScheduled,
            listLastVaccinationReports,
            listNextVaccinationAppointment,
            this.vaccinationList
          )
        );
      } catch (err) {
        this.handleError({ error: err, code: '0x0000003C' });
      }
    },

    async getMedicalProcedureData(medicalProcedureId) {
      let medicalProcedures = this.examinations.concat(this.vaccinations);
      let medicalProcedureData = medicalProcedures.find(val => val.id === medicalProcedureId);

      const retryDelay = 500;

      while (!medicalProcedureData) {
        await new Promise(resolve => {
          setTimeout(resolve, retryDelay);
        });
        medicalProcedures = this.examinations.concat(this.vaccinations);
        medicalProcedureData = medicalProcedures.find(val => val.id === medicalProcedureId);
      }

      return medicalProcedureData;
    },

    async loadReports(medicalProcedureId) {
      this.reportsLoading = true;

      const medicalProcedureData = await this.getMedicalProcedureData(medicalProcedureId);

      try {
        const queryName = `list${this.capitalizeFirstLetter(medicalProcedureData.type)}Reports`;
        const primaryKeyParamName = `${medicalProcedureData.type}InfoId`;

        const query = gql`
          query ${queryName}(
            $nextToken: String,
            $profileId: ID!,
            $${primaryKeyParamName}: ID!
          ) {
            ${queryName}(
              nextToken: $nextToken,
              profileId: $profileId,
              ${primaryKeyParamName}: $${primaryKeyParamName}
            ) {
              items {
                attachments {
                  id,
                  name,
                  url
                },
                date,
                id,
                procedureInfoId,
                profileId,
                scheduledDate,
                text
              }
            }
          }
        `;

        const variables = {
          nextToken: null,
          profileId: this.$auth.user.profileId,
          [primaryKeyParamName]: medicalProcedureId
        };

        const data = await this.$graphql.medicalProcedures.request(query, variables);
        if (data) {
          let reportData = lodash.cloneDeep(data[queryName].items);
          reportData.sort((left, right) => {
            return new Date(right.date) - new Date(left.date);
          });
          await this.setReports(reportData);
        }
      } catch (err) {
        this.logToFlutter({
          level: 'E',
          message: 'SPA HANDLE ERROR 61'
        });
        this.handleError({ error: err, code: '0x0000003D' });
      }

      let date = medicalProcedureData.lastReport;
      let scheduledReport = medicalProcedureData.scheduledReport;
      if (this.reports.length) {
        date = this.reports[0].date;
        scheduledReport = medicalProcedureData.intervalRules
          ? {
              procedureInfoId: medicalProcedureData.id,
              scheduledDate: this.getScheduledReport(medicalProcedureData.intervalRules, date)
            }
          : null;
      }

      await this.updateStaticReportData({
        medicalProcedureData: medicalProcedureData,
        date: date,
        scheduledReport: scheduledReport?.scheduledDate
      });

      this.reportsLoading = false;
    },

    async loadAppointments(medicalProcedureId) {
      this.$log('mixin/preventionData', 'loadAppointments start');
      this.appointmentsLoading = true;

      const medicalProcedureData = await this.getMedicalProcedureData(medicalProcedureId);

      try {
        const queryName = `list${this.capitalizeFirstLetter(
          medicalProcedureData.type
        )}Appointments`;
        const primaryKeyParamName = `${medicalProcedureData.type}InfoId`;

        const query = gql`
          query ${queryName}(
            $nextToken: String,
            $profileId: ID!,
            $${primaryKeyParamName}: ID!
          ) {
            ${queryName}(
              nextToken: $nextToken,
              profileId: $profileId,
              ${primaryKeyParamName}: $${primaryKeyParamName}
            ) {
              items {
                attachments {
                  id,
                  name,
                  url
                },
                dateTime,
                id,
                procedureInfoId,
                profileId,
                text
              }
            }
          }
        `;

        const variables = {
          nextToken: null,
          profileId: this.$auth.user.profileId,
          [primaryKeyParamName]: medicalProcedureId
        };

        const data = await this.$graphql.medicalProcedures.request(query, variables);
        if (data) {
          let appointmentData = lodash.cloneDeep(data[queryName].items);

          appointmentData.sort((left, right) => {
            return new Date(left.dateTime) - new Date(right.dateTime);
          });

          await this.setAppointments(appointmentData);
        }
      } catch (err) {
        this.logToFlutter({
          level: 'E',
          message: 'SPA HANDLE ERROR 62'
        });
        this.handleError({ error: err, code: '0x0000003E' });
      } finally {
        this.appointmentsLoading = false;
        this.$log('mixin/preventionData', 'loadAppointments end');
      }
    },

    async appointmentToReport({ procedureInfoId, appointmentId }) {
      this.$log('mixin/preventionData', 'appointmentToReport start');
      const medicalProcedures = this.examinations.concat(this.vaccinations);
      const medicalProcedureData = medicalProcedures.find(val => val.id === procedureInfoId);

      try {
        const mutationName = `change${this.capitalizeFirstLetter(
          medicalProcedureData.type
        )}AppointmentToReport`;
        const primaryKeyName = `${medicalProcedureData.type}AppointmentId`;

        const mutation = gql`
          mutation ${mutationName}(
            $profileId: ID!,
            $${primaryKeyName}: ID!
          ) {
            ${mutationName}(
              profileId: $profileId,
              ${primaryKeyName}: $${primaryKeyName}
            )
          }
        `;

        const variables = {
          profileId: this.$auth.user.profileId,
          [primaryKeyName]: appointmentId
        };

        const data = await this.$graphql.medicalProcedures.request(mutation, variables);
        if (data) {
          await Promise.all([
            this.loadAppointments(procedureInfoId),
            this.loadReports(procedureInfoId)
          ]);
        }
      } catch (err) {
        this.handleError({ error: err, code: '0x0000003F' });
      } finally {
        this.$log('mixin/preventionData', 'appointmentToReport end');
      }
    },
    isRequired(mapValue) {
      return mapValue.recommendation === 'recommended';
    },
    isAfterScheduledDate(mapValue) {
      return mapValue.nextAppointment
        ? mapValue.nextAppointmentMissed
        : mapValue.scheduledReport && mapValue.scheduledMissed;
    },
    isNotFilled(mapValue) {
      return !mapValue.scheduledReport;
    },
    hasInterval(mapValue) {
      return !!mapValue.intervalRules;
    },
    getScheduledReport(intervalRules, date) {
      if (!intervalRules.length) {
        return null;
      }

      const interval = intervalRules[0].interval;

      const number = +interval.slice(1, interval.length - 1);
      const intervalType = interval.slice(-1);
      let newDate;
      let reportDate = new Date(date);

      switch (intervalType) {
        case 'Y':
          newDate = new Date(reportDate.setFullYear(reportDate.getFullYear() + number))
            .toISOString()
            .slice(0, 10);
          break;
        case 'M':
          newDate = new Date(reportDate.setMonth(reportDate.getMonth() + number))
            .toISOString()
            .slice(0, 10);
      }

      return newDate;
    },
    ...mapActions({
      setExaminations: 'profile/prevention/setExaminations',
      setVaccinations: 'profile/prevention/setVaccinations',
      setReports: 'profile/prevention/setReports',
      setAppointments: 'profile/prevention/setAppointments',
      updateStaticReportData: 'profile/prevention/updateStaticReportData'
    }),
    getPreventionTitle(item) {
      switch (item) {
        case 'expectation':
          return this.$t('medicalProcedure.content.content1');
        case 'preparation':
          return this.$t('medicalProcedure.content.content2');
        case 'isItPaid':
          return this.$t('medicalProcedure.content.content3');
        case 'how':
          return this.$t('medicalProcedure.content.content4');
        case 'when':
          return this.$t('medicalProcedure.content.content5');
        case 'findConcerningQuestion':
          return this.$t('medicalProcedure.content.content6');
        case 'transmission':
          return this.$t('medicalProcedure.content.content7');
        case 'prevention':
          return this.$t('medicalProcedure.content.content8');
        case 'vaccineChoice':
          return this.$t('medicalProcedure.content.content9');
        default:
          return '';
      }
    }
  }
};
